/**
 * Journal, Agent Performance, and Blindspots Services
 */

import ApiClient from "../apiClient";

// Journal Service (Track 7A)
export interface JournalEntryItem {
  id: number;
  userId: string;
  type: "human" | "agent";
  date: string;
  erTrend?: number[];
  infractions?: string[];
  disciplineScore?: number;
  notes?: string;
  emotionalControlRating?: number;
  agentName?: string;
  proposalCount?: number;
  acceptedCount?: number;
  winRate?: number;
  avgRR?: number;
  totalPnl?: number;
  proposals?: Array<{
    id: string;
    agent: string;
    ticker: string;
    direction: "long" | "short";
    entry?: number;
    target?: number;
    stopLoss?: number;
    status: "proposed" | "accepted" | "rejected" | "expired";
    outcome?: "win" | "loss" | "breakeven" | null;
    pnl?: number;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export interface JournalSummaryResponse {
  totalEntries: number;
  avgDisciplineScore: number;
  totalInfractions: number;
  avgWinRate: number;
  avgRR: number;
  totalAgentPnl: number;
  streakDays: number;
  /** 8f: Proposals not taken that would have been profitable */
  missedTrades?: number;
}

export interface UserTradeActivityItem {
  id: string;
  contract: string;
  entryAt: string;
  qty: number;
  entryPrice: number;
  realizedPnl: number;
  origin: string | null;
}

export interface UserTradeActivityResponse {
  trades: UserTradeActivityItem[];
  source: string;
  from: string;
  to: string;
}

export class JournalService {
  constructor(private client: ApiClient) {}

  async listEntries(params?: {
    type?: "human" | "agent";
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
  }): Promise<{ entries: JournalEntryItem[]; total: number }> {
    try {
      const query = new URLSearchParams();
      if (params?.type) query.append("type", params.type);
      if (params?.limit) query.append("limit", params.limit.toString());
      if (params?.offset) query.append("offset", params.offset.toString());
      if (params?.from) query.append("from", params.from);
      if (params?.to) query.append("to", params.to);
      const suffix = query.toString() ? `?${query.toString()}` : "";
      return await this.client.get<{
        entries: JournalEntryItem[];
        total: number;
      }>(`/api/journal/entries${suffix}`);
    } catch {
      return { entries: [], total: 0 };
    }
  }

  async saveEntry(
    data: Partial<JournalEntryItem> & { type: "human" | "agent"; date: string },
  ): Promise<{ entryId: number }> {
    return this.client.post("/api/journal/entries", data);
  }

  async getSummary(days?: number): Promise<JournalSummaryResponse> {
    try {
      const suffix = days ? `?days=${days}` : "";
      return await this.client.get<JournalSummaryResponse>(
        `/api/journal/summary${suffix}`,
      );
    } catch {
      return {
        totalEntries: 0,
        avgDisciplineScore: 0,
        totalInfractions: 0,
        avgWinRate: 0,
        avgRR: 0,
        totalAgentPnl: 0,
        streakDays: 0,
      };
    }
  }

  async listTradeActivity(params: {
    from: string;
    to: string;
  }): Promise<UserTradeActivityResponse> {
    const fallback = {
      trades: [],
      source: "internal-user-ledger",
      from: params.from,
      to: params.to,
    };
    try {
      const query = new URLSearchParams({
        from: params.from,
        to: params.to,
      });
      const response = await this.client.get<{
        tradeActivity?: UserTradeActivityResponse;
      }>(`/api/data/performance?${query.toString()}`);
      return response.tradeActivity ?? fallback;
    } catch {
      return fallback;
    }
  }
}

// Agent Performance Service (Track 7)
export interface AgentPerformanceStatsItem {
  agentName: string;
  totalProposals: number;
  accepted: number;
  rejected: number;
  expired: number;
  executed: number;
  wins: number;
  losses: number;
  breakeven: number;
  winRate: number;
  avgRR: number;
  totalPnl: number;
  bestTrade: number;
  worstTrade: number;
}

export interface AgentPerformanceResponse {
  futures: AgentPerformanceStatsItem[];
  predictions: {
    total: number;
    resolved: number;
    wins: number;
    losses: number;
    winRate: number;
  };
  combined: {
    totalDecisions: number;
    totalWins: number;
    overallWinRate: number;
    totalPnl: number;
  };
  timestamp: string;
}

export class AgentPerformanceService {
  constructor(private client: ApiClient) {}

  async getPerformance(days: number = 30): Promise<AgentPerformanceResponse> {
    try {
      return await this.client.get<AgentPerformanceResponse>(
        `/api/agents/performance?days=${days}`,
      );
    } catch {
      return {
        futures: [],
        predictions: { total: 0, resolved: 0, wins: 0, losses: 0, winRate: 0 },
        combined: {
          totalDecisions: 0,
          totalWins: 0,
          overallWinRate: 0,
          totalPnl: 0,
        },
        timestamp: new Date().toISOString(),
      };
    }
  }
}

// [claude-code 2026-04-23] S30-T2: SessionJournalService — thin client for PUT /api/session-journal.
// Backend endpoint ships in T3; gracefully degrades until then.
import type { SessionJournal, SessionJournalDraft } from "../../../shared";

export class SessionJournalService {
  constructor(private client: ApiClient) {}

  /** Fetch the current user's journal entry for a given date (defaults to today). */
  async get(date?: string): Promise<SessionJournal | null> {
    try {
      const query = date ? `?date=${encodeURIComponent(date)}` : "";
      return await this.client.get<SessionJournal>(
        `/api/session-journal${query}`,
      );
    } catch {
      return null;
    }
  }

  /** Upsert the current user's journal entry. */
  async save(draft: SessionJournalDraft): Promise<SessionJournal | null> {
    try {
      return await this.client.put<SessionJournal>(
        "/api/session-journal",
        draft,
      );
    } catch {
      return null;
    }
  }
}

// Blindspots Service
export interface BlindspotItem {
  id: number;
  text: string;
  severity: "high" | "medium" | "low";
  /** 7-day rolling record: 'W' = win (avoided), 'L' = loss (triggered) */
  record?: Array<"W" | "L">;
  /** IV score of the referenced catalyst/headline (preferred over W/L% at the headline position) */
  ivScore?: number;
  /** Optional pointer to the catalyst in scored_riskflow_items */
  catalystRef?: string;
}

export interface BlindspotHistoryItem {
  id: string;
  date: string;
  pattern: string;
  evidence: string;
  correctiveAction: string;
  severity: number;
  source: "template" | "fluid";
  createdAt: string;
}

export interface LatestBlindspotsResponse {
  psych: { date: string | null; rows: BlindspotHistoryItem[] };
  trading: { date: string | null; rows: BlindspotHistoryItem[] };
}

export class BlindspotsService {
  constructor(private client: ApiClient) {}

  async getBlindspots(): Promise<{
    blindspots: BlindspotItem[];
    source: string;
  }> {
    try {
      return await this.client.get<{
        blindspots: BlindspotItem[];
        source: string;
      }>("/api/blindspots");
    } catch {
      return { blindspots: [], source: "error" };
    }
  }

  async getLatest(): Promise<LatestBlindspotsResponse> {
    try {
      return await this.client.get<LatestBlindspotsResponse>(
        "/api/blindspots/latest",
      );
    } catch {
      return {
        psych: { date: null, rows: [] },
        trading: { date: null, rows: [] },
      };
    }
  }
}
